import { useState } from 'react'
import { QueryFunctionContext, useQuery, UseQueryOptions } from 'react-query'
import { useQueryPageParam, useQueryParams, useQueryUtils } from 'utils-next/routes'
import { PaginationOptions } from 'utils-next/types'

import { PaginatedSearchFunction, PaginatedSearchOptions, PaginatedSearchStore } from './PaginatedSearch.types'

const DEFAULT_LIMIT = 10
const DEFAULT_PAGE = 0

export function usePaginatedSearch<Entity, Filters extends Record<string, any>>(
  entityId: string,
  searchEntitiesWithFilters: PaginatedSearchFunction<Entity, Filters>,
  {
    initialPage = DEFAULT_PAGE,
    initialFilters,
    limit = DEFAULT_LIMIT,
    urlSync = true,
  }: PaginatedSearchOptions<Filters>,
  queryOptions?: UseQueryOptions<Entity[]>
): PaginatedSearchStore<Entity, Filters> {
  const { currentPage, goToPage, goToNextPage, goToPreviousPage, goToInitialPage } = usePaginatedSearchPage({
    initialPage,
    urlSync,
  })
  const { filters, setFilters } = usePaginatedSearchFilters<Filters>({
    initialFilters,
    onChangeFilters: goToInitialPage,
    urlSync,
  })
  const { data, status, isPreviousData, refetch } = useQuery(
    [entityId, filters, { currentPage, limit }],
    ({ queryKey }: QueryFunctionContext<[string, Filters, PaginationOptions]>) =>
      searchEntitiesWithFilters(...queryKey),
    { keepPreviousData: true, ...queryOptions }
  )

  const state = {
    filters,
    currentPage,
    limit,
    data,
    isPreviousData,
    status,
  }

  const actions = {
    refetch,
    setFilters,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToInitialPage,
  }

  return { state, actions }
}

function usePaginatedSearchFilters<Filters extends Record<string, any>>({
  urlSync,
  initialFilters,
  onChangeFilters,
}: {
  urlSync: boolean
  initialFilters: Filters
  onChangeFilters: Function
}) {
  const [stateFilters, setInternalFilters] = useState<Filters>(initialFilters)
  const queryParamsFilters = useQueryParams<Filters>()
  const { replaceQueryParams } = useQueryUtils()

  const filters = urlSync && Object.keys(queryParamsFilters).length ? queryParamsFilters : stateFilters
  const setFilters = (newFilters: Partial<Filters>) => {
    const mergedFilters = { ...filters, ...newFilters }
    if (urlSync) {
      replaceQueryParams(mergedFilters)
    } else {
      setInternalFilters(mergedFilters)
    }
    onChangeFilters()
  }
  return {
    filters,
    setFilters,
  }
}

function usePaginatedSearchPage({ initialPage, urlSync }: { initialPage: number; urlSync: boolean }) {
  const [stateCurrentPage, setCurrentPage] = useState(initialPage)
  const { replaceQueryPageParam } = useQueryUtils()
  const queryParamsCurrentPage = useQueryPageParam()

  const currentPage = urlSync ? queryParamsCurrentPage : stateCurrentPage

  const goToPage = (page: number) => {
    const boundedPage = Math.max(page, 0)
    if (urlSync) {
      replaceQueryPageParam(boundedPage)
    } else {
      setCurrentPage(boundedPage)
    }
  }
  const goToNextPage = () => goToPage(currentPage + 1)
  const goToPreviousPage = () => goToPage(currentPage - 1)
  const goToInitialPage = () => goToPage(initialPage)

  return {
    currentPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToInitialPage,
  }
}
